import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage} from 'react-intl';

import {Constants} from 'utils/constants';
import * as Utils from 'utils/utils';
import * as UserUtils from 'mattermost-redux/utils/user_utils';
import EventEmitter from 'mattermost-redux/utils/event_emitter';
import {Constants as StatistConstants} from '@time-webkit/statist';

import DropdownIcon from 'components/widgets/icons/fa_dropdown_icon';
import Menu from 'components/widgets/menu/menu';
import MenuWrapper from 'components/widgets/menu/menu_wrapper';
import OverlayTrigger from 'components/overlay_trigger';
import Tooltip from 'components/tooltip';

const ROWS_FROM_BOTTOM_TO_OPEN_UP = 3;

export default class ChannelMembersDropdown extends React.PureComponent {
    static get propTypes() {
        return {
            isDefaultChannel: PropTypes.bool.isRequired,
            channel: PropTypes.object.isRequired,
            user: PropTypes.object.isRequired,
            currentUserId: PropTypes.string.isRequired,
            channelMember: PropTypes.object.isRequired,
            canChangeMemberRoles: PropTypes.bool.isRequired,
            canRemoveMember: PropTypes.bool.isRequired,
            index: PropTypes.number.isRequired,
            totalUsers: PropTypes.number.isRequired,
            channelAdminLabel: PropTypes.element,
            channelMemberLabel: PropTypes.element,
            guestLabel: PropTypes.element,
            guestPlusLabel: PropTypes.element,
            actions: PropTypes.shape({
                getChannelStats: PropTypes.func.isRequired,
                updateChannelMemberSchemeRoles: PropTypes.func.isRequired,
                removeChannelMember: PropTypes.func.isRequired,
                getChannelMember: PropTypes.func.isRequired,
            }).isRequired,
        };
    }

    constructor(props) {
        super(props);

        this.state = {
            serverError: null,
            removing: false,
        };
    }

    handleRemoveFromChannel = () => {
        if (this.state.removing) {
            return;
        }

        const {actions, channel, user} = this.props;
        this.setState({removing: true});

        actions.removeChannelMember(channel.id, user.id).then((result) => {
            if (result.error) {
                this.setState({
                    serverError: result.error.message,
                    removing: false,
                });
                return;
            }

            this.setState({removing: false});
            actions.getChannelStats(channel.id);

            EventEmitter.emit(StatistConstants.AnalyticEvents.CHANNEL_MEMBER_REMOVED, this.props.index);
        });
    };

    handleMakeChannelMember = async () => {
        const {error} = await this.props.actions.updateChannelMemberSchemeRoles(this.props.channel.id, this.props.user.id, true, false);
        if (error) {
            this.setState({serverError: error.message});
        } else {
            this.props.actions.getChannelStats(this.props.channel.id);
            this.props.actions.getChannelMember(this.props.channel.id, this.props.user.id);
        }
    };

    handleMakeChannelAdmin = async () => {
        const {error} = await this.props.actions.updateChannelMemberSchemeRoles(this.props.channel.id, this.props.user.id, true, true);
        if (error) {
            this.setState({serverError: error.message});
        } else {
            this.props.actions.getChannelStats(this.props.channel.id);
            this.props.actions.getChannelMember(this.props.channel.id, this.props.user.id);
        }
    };

    renderRole(isChannelAdmin, isGuestPlus, isGuest) {
        if (isChannelAdmin) {
            if (this.props.channelAdminLabel) {
                return this.props.channelAdminLabel;
            }
            return (
                <FormattedMessage
                    id='channel_members_dropdown.channel_admin'
                    defaultMessage='Channel Admin'
                />
            );
        } else if (isGuest) {
            if (this.props.guestLabel) {
                return this.props.guestLabel;
            }
            return (
                <FormattedMessage
                    id='channel_members_dropdown.channel_guest'
                    defaultMessage='Channel Guest'
                />
            );
        } else if (isGuestPlus) {
            if (this.props.guestPlusLabel) {
                return this.props.guestPlusLabel;
            }
            return (
                <FormattedMessage
                    id='channel_members_dropdown.channel_guest_plus'
                    defaultMessage='Channel Guest +'
                />
            );
        }

        if (this.props.channelMemberLabel) {
            return this.props.channelMemberLabel;
        }
        return (
            <FormattedMessage
                id='channel_members_dropdown.channel_member'
                defaultMessage='Channel Member'
            />
        );
    }

    isChannelAdmin(channelMember) {
        return UserUtils.isChannelAdmin(channelMember.roles) || channelMember.scheme_admin;
    }

    render() {
        const {
            index,
            totalUsers,
            channelMember,
            user,
            channel,
            isDefaultChannel,
            currentUserId,
            canChangeMemberRoles,
            canRemoveMember,
        } = this.props;

        if (user.id === currentUserId) {
            return null;
        }

        const {serverError} = this.state;
        const isChannelAdmin = this.isChannelAdmin(channelMember);
        const isGuest = UserUtils.isGuest(user.roles);
        const isGuestPlus = UserUtils.isGuestPlus(user.roles);
        const isAnyGuest = isGuest || isGuestPlus;
        const isMember = !isChannelAdmin && !isAnyGuest;
        const currentRole = this.renderRole(isChannelAdmin, isGuestPlus, isGuest);

        if (user.remote_id) {
            const sharedTooltip = (
                <Tooltip id='sharedTooltip'>
                    <FormattedMessage
                        id='shared_user_indicator.tooltip'
                        defaultMessage='From trusted organizations'
                    />
                </Tooltip>
            );

            return (
                <div className='more-modal__shared-actions'>
                    <OverlayTrigger
                        delayShow={Constants.OVERLAY_TIME_DELAY}
                        placement='bottom'
                        overlay={sharedTooltip}
                    >
                        <span>
                            <FormattedMessage
                                id='channel_members_dropdown.shared_member'
                                defaultMessage='Shared Member'
                            />
                            <i className='shared-user-icon icon-circle-multiple-outline'/>
                        </span>
                    </OverlayTrigger>
                </div>
            );
        }

        const canMakeUserChannelMember = canChangeMemberRoles && isChannelAdmin;
        const canMakeUserChannelAdmin = canChangeMemberRoles && isMember;
        const canRemoveUserFromChannel =
            canRemoveMember && (!channel.group_constrained || user.is_bot) && (!isDefaultChannel || isAnyGuest);

        if (canMakeUserChannelMember || canMakeUserChannelAdmin || canRemoveUserFromChannel) {
            const removeMenu = (
                <Menu.ItemAction
                    show={canRemoveUserFromChannel}
                    onClick={this.handleRemoveFromChannel}
                    text={Utils.localizeMessage('channel_members_dropdown.remove_from_channel', 'Remove from Channel')}
                />
            );
            const makeAdminMenu = (
                <Menu.ItemAction
                    id={`${user.username}-make-channel-admin`}
                    show={canMakeUserChannelAdmin}
                    onClick={this.handleMakeChannelAdmin}
                    text={Utils.localizeMessage('channel_members_dropdown.make_channel_admin', 'Make Channel Admin')}
                />
            );
            const makeMemberMenu = (
                <Menu.ItemAction
                    id={`${user.username}-make-channel-member`}
                    show={canMakeUserChannelMember}
                    onClick={this.handleMakeChannelMember}
                    text={Utils.localizeMessage('channel_members_dropdown.make_channel_member', 'Make Channel Member')}
                />
            );
            return (
                <MenuWrapper>
                    <button
                        className='dropdown-toggle theme color--link style--none'
                        type='button'
                    >
                        <span className='sr-only'>{user.username}</span>
                        <span>{currentRole} </span>
                        <DropdownIcon/>
                    </button>
                    <Menu
                        id='channelMembersDropdown'
                        openLeft={true}
                        openUp={totalUsers > ROWS_FROM_BOTTOM_TO_OPEN_UP && totalUsers - index <= ROWS_FROM_BOTTOM_TO_OPEN_UP}
                        ariaLabel={Utils.localizeMessage('channel_members_dropdown.menuAriaLabel', 'Change the role of channel member')}
                    >
                        {canMakeUserChannelMember ? makeMemberMenu : null}
                        {canMakeUserChannelAdmin ? makeAdminMenu : null}
                        {canRemoveUserFromChannel ? removeMenu : null}
                        {serverError && (
                            <div className='has-error'>
                                <label className='has-error control-label'>{serverError}</label>
                            </div>
                        )}
                    </Menu>
                </MenuWrapper>
            );
        }

        if (isDefaultChannel) {
            return <div />;
        }

        return (
            <div>
                {currentRole}
            </div>
        );
    }
}
